import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { API_BASE_URL } from '../../utils/apiConfig';
import './RsiHeatmapGrid.css';

const formatVolume = (volume) => {
  if (volume >= 1e9) return `$${(volume / 1e9).toFixed(2)}B`;
  if (volume >= 1e6) return `$${(volume / 1e6).toFixed(2)}M`;
  if (volume >= 1e3) return `$${(volume / 1e3).toFixed(0)}K`;
  return `$${volume.toFixed(2)}`;
};

const formatPrice = (price) => {
  if (price >= 1000) return `$${price.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
  if (price >= 1) return `$${price.toFixed(2)}`;
  if (price >= 0.01) return `$${price.toFixed(4)}`;
  return `$${price.toFixed(6)}`;
};

const getCategoryClass = (category) => {
  const classes = {
    extremeOversold: 'rsi-extreme-oversold',
    oversold: 'rsi-oversold',
    neutral: 'rsi-neutral',
    overbought: 'rsi-overbought',
    extremeOverbought: 'rsi-extreme-overbought',
    unknown: 'rsi-unknown',
  };
  return classes[category] || 'rsi-unknown';
};

function RsiHeatmapGrid() {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hoveredCoin, setHoveredCoin] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/rsi-heatmap`);
      const result = await response.json();

      if (result.success) {
        setData(result);
        setError(null);
      } else {
        setError(result.error || 'Failed to load data');
      }
    } catch (err) {
      setError('Failed to fetch RSI data');
      console.error('[RSI] Fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();

    // Refresh every 30 seconds
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [fetchData]);

  if (loading) {
    return (
      <div className="rsi-heatmap-container">
        <div className="rsi-loading">
          <div className="rsi-loading-spinner"></div>
          <span>{t('rsi.loading', 'Loading RSI data...')}</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rsi-heatmap-container">
        <div className="rsi-error">
          <span>{error}</span>
          <button onClick={fetchData}>{t('rsi.retry', 'Retry')}</button>
        </div>
      </div>
    );
  }

  if (!data || !data.coins) {
    return null;
  }

  return (
    <div className="rsi-heatmap-container">
      {/* Legend - ordered: red → yellow → green → gray */}
      <div className="rsi-legend">
        <div className="rsi-legend-item">
          <span className="rsi-legend-color rsi-extreme-overbought"></span>
          <span>{t('rsi.extremeOverbought', 'RSI > 80')}</span>
        </div>
        <div className="rsi-legend-item">
          <span className="rsi-legend-color rsi-overbought"></span>
          <span>{t('rsi.overbought', 'RSI 70-80')}</span>
        </div>
        <div className="rsi-legend-item">
          <span className="rsi-legend-color rsi-extreme-oversold"></span>
          <span>{t('rsi.extremeOversold', 'RSI < 20')}</span>
        </div>
        <div className="rsi-legend-item">
          <span className="rsi-legend-color rsi-oversold"></span>
          <span>{t('rsi.oversold', 'RSI 20-30')}</span>
        </div>
        <div className="rsi-legend-item">
          <span className="rsi-legend-color rsi-neutral"></span>
          <span>{t('rsi.neutral', 'RSI 30-70')}</span>
        </div>
      </div>

      {/* Grid */}
      <div className="rsi-grid">
        {data.coins.map((coin, index) => (
          <div
            key={coin.symbol}
            className={`rsi-cell ${getCategoryClass(coin.category)}`}
            onMouseEnter={() => setHoveredCoin(coin)}
            onMouseLeave={() => setHoveredCoin(null)}
          >
            <div className="rsi-cell-symbol">{coin.symbol}</div>
            <div className="rsi-cell-value">
              {coin.rsi !== null ? coin.rsi.toFixed(1) : 'N/A'}
            </div>
          </div>
        ))}
      </div>

      {/* Tooltip */}
      {hoveredCoin && (
        <div className="rsi-tooltip">
          <div className="rsi-tooltip-header">{hoveredCoin.symbol}/USDT</div>
          <div className="rsi-tooltip-row">
            <span>{t('rsi.price', 'Price')}:</span>
            <span>{formatPrice(hoveredCoin.price)}</span>
          </div>
          <div className="rsi-tooltip-row">
            <span>RSI (14):</span>
            <span className={getCategoryClass(hoveredCoin.category)}>
              {hoveredCoin.rsi !== null ? hoveredCoin.rsi.toFixed(1) : 'N/A'}
            </span>
          </div>
          <div className="rsi-tooltip-row">
            <span>{t('rsi.change24h', '24h Change')}:</span>
            <span className={hoveredCoin.priceChange >= 0 ? 'positive' : 'negative'}>
              {hoveredCoin.priceChange >= 0 ? '+' : ''}{hoveredCoin.priceChange.toFixed(2)}%
            </span>
          </div>
          <div className="rsi-tooltip-row">
            <span>{t('rsi.volume24h', '24h Volume')}:</span>
            <span>{formatVolume(hoveredCoin.volume)}</span>
          </div>
        </div>
      )}

      {/* Footer info */}
      <div className="rsi-footer-info">
        <span>
          {t('rsi.dataSource', 'Data')}: Binance | RSI {t('rsi.period', 'Period')}: {data.period} | {t('rsi.timeframe', 'Timeframe')}: {data.interval}
        </span>
        {data.lastUpdate && (
          <span className="rsi-last-update">
            {t('rsi.lastUpdate', 'Updated')}: {new Date(data.lastUpdate).toLocaleTimeString()}
          </span>
        )}
      </div>
    </div>
  );
}

export default RsiHeatmapGrid;
